import { html } from 'lit-element';
import { ifNotNull } from '../../utils/directives';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
  nextUniqueId,
  coerceToBoolean,
} from '../../shared/base';

/**
 * @component {kat-radiobutton} KatalRadiobutton Allows customers to choose only one of a predefined set of options. Only a single radio button may be selected in a list of options.
 * @guideline Do Group radio buttons by the name attribute.  Radio buttons with the same name will function together as a group
 * @guideline Do Accessibility requirements for Radio buttons require they should be inside an element with role="radiogroup".
 * @event change Fires when a radio button has been selected. The detail contains a checked property with the current state
 * @event focus Fires when the user focuses into the radio button.
 * @event blur Fires when the user stops focusing the radio button.
 * @example Basic {"label": "Option1", "value":"value1", "checked":"true", "constraint-label":""}
 * @example WithConstraint {"label": "Option1", "value":"value1", "constraint-label":"This is the first option"}
 * @example Disabled {"label": "Option1", "value":"value1", "disabled":"true", "constraint-label":""}
 * @status Production
 * @theme flo
 */
class KatalRadiobutton extends KatLitElement {
  _thisUniqueId = nextUniqueId();
  _constraintId = nextUniqueId();
  _checked = false;

  static get properties() {
    return {
      /**
       * @param {string} value The value of the radio button
       * @required
       */
      value: { type: String, reflect: true },

      /**
       * @param {string} label The label displayed to the user for
       */
      label: { type: String, reflect: true },

      /**
       * @param {string} constraint-label Provides users with more information about what they enter into the input
       */
      constraintLabel: {
        type: String,
        attribute: 'constraint-label',
        reflect: true,
      },

      /**
       * @param {boolean} checked Determines of the radio button is selected or not
       */
      checked: { type: Boolean, converter: booleanConverter },

      /**
       * @param {string} unique-id The id of the form element
       */
      uniqueId: { type: String, attribute: 'unique-id', reflect: true },

      /**
       * @param {string} name The form name of the element
       */
      name: { type: String, reflect: true },

      /**
       * @param {string} kat-aria-label The label used for screen readers. By
       * default, screen readers will announce the text from the `label`
       * property. Use `kat-aria-label` when you want screen readers to announce
       * text different from the `label` property.
       */
      katAriaLabel: {
        type: String,
        attribute: 'kat-aria-label',
        reflect: true,
      },

      /**
       * @param {string} kat-aria-labelledby The ID of an element used as a
       * label for screen readers. Use this if you are using your own label
       * element as the label of this kat-radiobutton.
       */
      katAriaLabelledby: {
        type: String,
        attribute: 'kat-aria-labelledby',
        reflect: true,
      },

      /**
       * @param {boolean} disabled prevents the user from changing the value of an input and prevents events from being triggered
       */
      disabled: { type: Boolean, converter: booleanConverter, reflect: true },
    };
  }

  constructor() {
    super();

    this._uniqueId = this._thisUniqueId;
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    this._input = this.getElementsByTagName('input')[0];
    this._input.checked = this._checked;
  }

  // pass the `checked` value through from the underlying input
  get checked() {
    if (this._input) {
      return this._input.checked;
    }
    return this._checked;
  }

  // set `checked` on the underlying input if we have rendered, otherwise store
  // it on `_checked` for now.
  set checked(val) {
    val = coerceToBoolean(val);
    const oldVal = this.checked;
    this._checked = val;
    if (this._input) {
      this._input.checked = val;
    }
    this.requestUpdate('checked', oldVal);
  }

  get uniqueId() {
    return this._uniqueId;
  }

  set uniqueId(val) {
    const oldVal = this.uniqueId;
    if (!val) {
      val = this._thisUniqueId;
    }
    this._uniqueId = val;
    this.requestUpdate('uniqueId', oldVal);
  }

  get disabled() {
    return this._disabled;
  }

  set disabled(val) {
    val = coerceToBoolean(val);
    const oldVal = this.disabled;
    this._disabled = val;
    this.requestUpdate('disabled', oldVal);
  }

  handleClick() {
    if (!this.disabled) {
      this.click();
      this.focus();
    }
  }

  handleFocus(e) {
    e.stopPropagation();
    this.dispatchCustomEvent('focus', {}, true);
  }

  handleBlur(e) {
    e.stopPropagation();
    this.dispatchCustomEvent('blur', {}, true);
  }

  handleChange(e) {
    e.stopPropagation();
    this.dispatchCustomEvent('change', {
      checked: true,
      value: this.value,
    });
  }

  /**
   * Call to programmatically focus the radio button.
   * @katalmethod
   */
  focus() {
    if (!this.disabled && this._input) {
      this._input.focus();
    }
  }

  /**
   * Call to programmatically blur the radio button.
   * @katalmethod
   */
  blur() {
    if (!this.disabled && this._input) {
      this._input.blur();
    }
  }

  /**
   * Call to programmatically click the radio button.
   * @katalmethod
   */
  click() {
    if (!this.disabled && this._input) {
      this._input.click();
    }
  }

  render() {
    return html`
      <div @click=${this.handleClick}>
        <div>
          <input
            class="kat-radiobutton-pos"
            type="radio"
            id="${this._uniqueId}"
            name="${ifNotNull(this.name)}"
            value="${ifNotNull(this.value)}"
            ?disabled=${this.disabled}
            aria-label="${ifNotNull(this.katAriaLabel)}"
            aria-describedby="${this._constraintId}"
            aria-labelledby="${ifNotNull(this.katAriaLabelledby)}"
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
            @change=${this.handleChange}
          />
          <span class="kat-radiobutton-pos kat-radiobutton-icon"></span>
        </div>
        <div class="${`labels ${this.disabled ? 'disabled' : ''}`}">
          <kat-label
            class="text"
            for="${this._uniqueId}"
            .text=${this.label}
          ></kat-label>
          <kat-label
            id="${this._constraintId}"
            class="constraint"
            for="${this._uniqueId}"
            variant="constraint"
            .text=${this.constraintLabel}
          ></kat-label>
        </div>
      </div>
    `;
  }
}
// coerceBooleanClassProperties(KatalRadiobutton);
KatalComponent.register('kat-radiobutton', KatalRadiobutton);
